import { debounce } from "lodash-es";
import { ref } from "vue";

/**
 * Async-search engine: owns debounce, loading state, and stale-result
 * race-guarding around a caller-supplied fetcher. The shared lifecycle behind
 * SearchSelect's `:search` prop (and any future async typeahead).
 *
 * @param {(query: string) => Promise<Array>} fetchFn
 * @param {{ debounceMs?: number }} [options]
 */
export function useAsyncSearch(fetchFn, { debounceMs = 300 } = {}) {
  const items = ref([]);
  const loading = ref(false);
  const hasSearched = ref(false);

  // Monotonic guard so a slow earlier request can't overwrite a newer result.
  let seq = 0;

  async function run(query) {
    const mine = ++seq;
    loading.value = true;
    try {
      const results = await fetchFn(query);
      if (mine === seq) {
        items.value = results ?? [];
        hasSearched.value = true;
      }
    } finally {
      if (mine === seq) {
        loading.value = false;
      }
    }
  }

  const debouncedRun = debounce(run, debounceMs);

  return {
    items,
    loading,
    hasSearched,
    run,
    debouncedRun,
    cancel: debouncedRun.cancel,
  };
}
